/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 24-07-2026
 *
 * Uitlegblok bij een nodemenu-item, opvolger van de ng1-directive `node-menu-uitleg`. Er is er één
 * per modelcanvas: het canvas houdt bij welk menu-item gehoverd wordt en geeft dat hier als input
 * binnen. Het blok plaatst zichzelf onder de menulijst waar dat item in zit (het anker), en klapt
 * naar boven wanneer het er onder niet past — het canvas kapt af op zijn randen.
 *
 * De vertragingen bij tonen en verbergen komen uit de ng1-versie: de verbergvertraging is er zodat je
 * met de muis van het menu-item naar het uitlegblok kunt bewegen zonder dat het blok wegvalt.
 */
import {ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, inject, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {KeyedTimeout} from 'sihw-ngx';
import {zoekIngredientUitleg} from '../../../lib/ingredient-uitleg';

/**
 * Het gehoverde menu-item: het type waarvoor uitleg gezocht wordt en de menulijst waaronder het blok
 * komt te staan.
 */
export interface MenuUitlegMelding {
    type: string;
    anker: HTMLElement;
}

/**
 * De plaatsing van het blok binnen het canvas: verticaal vanaf de bovenkant óf vanaf de onderkant
 * (bij een omgeklapt blok), horizontaal altijd vanaf links. In pixels.
 */
interface UitlegPositie {
    vanBoven: number | null;
    vanOnder: number | null;
    links: number;
}

/**
 * Vertraging in milliseconden voor het tonen en voor het verbergen van het blok.
 */
const VERTRAGING_MS = 150;

/**
 * Ruimte tussen de menulijst en het uitlegblok, en de inspringing links.
 */
const MARGE = 8;

/**
 * Hoogte waarmee gerekend wordt bij de keuze onder/boven het menu: de `max-height` van de ingeklapte
 * tekst plus de padding eromheen. De echte hoogte is nog niet bekend op het moment van plaatsen.
 */
const MAX_HOOGTE_INGEKLAPT = 132;

@Component({
    selector: 'dl-node-menu-uitleg',
    standalone: true,
    templateUrl: './dl-node-menu-uitleg.component.html',
    styleUrls: ['./dl-node-menu-uitleg.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DlNodeMenuUitlegComponent implements OnChanges, OnDestroy {

    /**
     * Het gehoverde menu-item, of `null` als de muis nergens op een item met uitleg staat.
     */
    @Input() melding: MenuUitlegMelding | null = null;

    /**
     * Het effectief modelleerniveau, voor het opzoeken van de tekst.
     */
    @Input() modelleerniveau = 0;

    /**
     * De korte uitlegtekst, inclusief de "lees meer"-staart. Leeg zolang er niets getoond wordt.
     */
    protected readonly kort = signal('');

    /**
     * De volledige uitlegtekst, of leeg als de tekst niet uitklapbaar is.
     */
    protected readonly vol = signal('');

    /**
     * Of de volledige tekst getoond wordt, na een klik op het blok.
     */
    protected readonly toonVol = signal(false);

    /**
     * Of het blok alle inhoud toont in plaats van de vervaagde eerste regels; aan zolang de muis
     * op het blok staat.
     */
    protected readonly allesZichtbaar = signal(false);

    /**
     * Of de fade-in ingezet is; stuurt de opacity-transitie aan.
     */
    protected readonly wordtZichtbaar = signal(false);

    /**
     * De plaatsing van het blok binnen het canvas.
     */
    protected readonly positie = signal<UitlegPositie>({vanBoven: 0, vanOnder: null, links: 0});

    private readonly translateService = inject(TranslateService);
    private readonly host: HTMLElement = inject(ElementRef).nativeElement;

    /**
     * De toon-/verbergtimer ('toonverberg'); er loopt er nooit meer dan één.
     */
    private readonly timers = new KeyedTimeout();

    ngOnChanges(): void {
        this.timers.clear('toonverberg');
        switch (true) {
            case !this.melding:
                //vertraagd verbergen, zodat de muis van het menu-item naar het blok kan bewegen
                this.timers.set('toonverberg', () => this.verberg(), VERTRAGING_MS);
                break;
            case !!this.kort():
                //er staat al een blok: meteen omschakelen naar de nieuwe inhoud
                this.toon();
                break;
            default:
                this.timers.set('toonverberg', () => this.toon(), VERTRAGING_MS);
        }
    }

    ngOnDestroy(): void {
        this.timers.clearAll();
    }

    /**
     * Houdt het blok staan zolang de muis erop staat, en toont alle inhoud.
     */
    protected opPointerEnter(): void {
        this.timers.clear('toonverberg');
        this.allesZichtbaar.set(true);
    }

    /**
     * Valt terug op de korte weergave en zet het vertraagd verbergen weer in gang.
     */
    protected opPointerLeave(): void {
        this.allesZichtbaar.set(false);
        this.toonVol.set(false);
        this.timers.set('toonverberg', () => this.verberg(), VERTRAGING_MS);
    }

    /**
     * Toont de volledige tekst als die er is. De gebeurtenis gaat niet verder, zodat de klik het
     * menu niet sluit.
     */
    protected opKlik(gebeurtenis: Event): void {
        this.blokkeer(gebeurtenis);
        if (this.vol()) {
            this.toonVol.set(true);
        }
    }

    /**
     * Houdt een pointer-gebeurtenis binnen het blok, zodat listeners op het canvas of de body hem
     * niet oppikken en het menu sluiten.
     */
    protected blokkeer(gebeurtenis: Event): void {
        gebeurtenis.preventDefault();
        gebeurtenis.stopPropagation();
    }

    /**
     * Zoekt de tekst op, bepaalt de plaatsing en toont het blok. Verbergt in plaats daarvan als er
     * geen tekst is, of als het anker geen oppervlak meer heeft: dat laatste betekent dat het menu
     * gesloten is of dat de node-volger het verborgen heeft (bijvoorbeeld na een condtag-wissel).
     */
    private toon(): void {
        if (!this.melding) {
            return;
        }
        const ankerRect = this.melding.anker.getBoundingClientRect();
        const gevonden = zoekIngredientUitleg(this.translateService, this.melding.type, this.modelleerniveau);
        if (!gevonden.kort || (!ankerRect.width && !ankerRect.height)) {
            this.verberg();
            return;
        }
        this.kort.set(gevonden.kort);
        this.vol.set(gevonden.vol);
        this.toonVol.set(false);
        this.allesZichtbaar.set(false);
        this.plaats(ankerRect);
        //eerst met opacity 0 laten renderen, daarna pas de fade-in aanzetten
        this.wordtZichtbaar.set(false);
        setTimeout(() => this.wordtZichtbaar.set(true));
    }

    /**
     * Haalt het blok weg.
     */
    private verberg(): void {
        this.kort.set('');
        this.vol.set('');
        this.toonVol.set(false);
        this.allesZichtbaar.set(false);
        this.wordtZichtbaar.set(false);
    }

    /**
     * Zet het blok onder de menulijst, of erboven wanneer daar te weinig ruimte is. Bij een omgeklapt
     * blok wordt vanaf de onderkant gepositioneerd, zodat de eigen hoogte er niet toe doet.
     */
    private plaats(ankerRect: DOMRect): void {
        const canvasRect = (this.host.parentElement as HTMLElement).getBoundingClientRect();
        const past = ankerRect.bottom + MARGE + MAX_HOOGTE_INGEKLAPT <= canvasRect.bottom;
        this.positie.set({
            vanBoven: past ? ankerRect.bottom - canvasRect.top + MARGE : null,
            vanOnder: past ? null : canvasRect.bottom - ankerRect.top + MARGE,
            links: ankerRect.left - canvasRect.left + MARGE
        });
    }
}
